// © Microsoft Corporation. All rights reserved.

import { ExperienceDeferralEventTypes, ExperienceLoadingState } from "../data-model";

import { registerResetCallback } from "@msnews/core";

/**
 * Identifies a referenced experience and its dependents
 */
export interface ExperienceDependents {
    /**
     * Determines the state of deferral identifier experience.
     */
    currentState: ExperienceLoadingState;

    /**
     * The dependent set of experiences and its associated state mapped to a deferred event type.
     * An experience moves from deferred to resolvedForLoad or resolvedForRender state when deferral identifier experienceType/Group currentState matches deferred state.
     */
    referencedDependentsDeferralMap: Map<ExperienceDeferralEventTypes, Map<string, ExperienceLoadingState>>
}

// Keeps track of Experience Identifier to its referenced dependents mapping for loadAfter/renderAfter scenario
// Experience Identifier, acts as a reference to resolve its dependents.
// The key (experienceIdentifier) should be either experienceType or experienceGroup
export let experienceDeferralDependencyMap: Map<string, ExperienceDependents> = new Map<string, ExperienceDependents>();

/**
 * Register a callback to reset the experienceDeferralDependencyMap.
 * The callback is registered once when the module is loaded and invoked once on the server side at the beginning of each request.
 */
registerResetCallback(() => {
    experienceDeferralDependencyMap = new Map<string, ExperienceDependents>();
});

/**
 * Checks if experience's dependency is resolved
 * @returns true if dependent experience has been resolved.
 * @param experienceDependency - dependent experience
 * @param deferralEventType - deferral EventType
 */
export function isExperienceDependencyResolved(experienceDependency: string, deferralEventType: ExperienceDeferralEventTypes): boolean {

    const dependents = experienceDeferralDependencyMap.get(experienceDependency);
    return !dependents
        || dependents.currentState >= deferralEventType;
}